import { createVueApp } from '../vue';
import asyncComponent from '@helpers/async-component';
import '@/legacy/shop';
import { provide, ref, computed, onMounted, onBeforeMount, nextTick, type Ref } from 'vue';
import { useCartStore } from '@stores/cart';
import { OPEN_FAST_BUY_MODAL_KEY } from '@helpers/injection-keys';
import { scrollToAnchor } from '@helpers/scroll-to-anchor';
import CartCounter from '@components/shop/CartCounter.vue';
import ModalWindow from '@components/shared/ModalWindow.vue';
import SearchBar from '@components/landing/ui/SearchBar.vue';
import PopupCart from '@components/shop/PopupCart.vue';
import VCartDropdown from '@components/shop/VCartDropdown.vue';
import CartNotification from '@components/shop/CartNotification.vue';
import HamburgerMenu from '../components/shared/HamburgerMenu.vue';
import MobileFooterMenu from '../components/shared/MobileFooterMenu.vue';
import VProfileDropdown from '../components/shared/VProfileDropdown.vue';
import CookieNotice from '@components/shared/CookieNotice.vue';
import VNotifications from '@components/shared/VNotifications.vue';
import RequisitesCopyButton from '@components/shop/RequisitesCopyButton.vue';
import initLazyload from '@helpers/init-lazyload';
import { trackUser, reachGoal, awaitForMetrikaScript, GOALS } from '@services/metrika';

// Sync = shop chrome (header / cart toast / mobile shell).
// Lazy = page islands + PDP modals (FoundCheaper / FastBuy) — see Wave C.
document.addEventListener('DOMContentLoaded', () => {
  initLazyload(document.querySelectorAll('.lazyload'));
});

trackUser();

type ModalExpose = { open?: () => void };

/** Poll lazy modal expose until open() is ready (~1s). */
const MODAL_OPEN_POLL_ATTEMPTS = 40;
const MODAL_OPEN_POLL_MS = 25;

/** Wait for async modal chunk + expose before calling open(). */
async function openAsyncModal(modalRef: Ref<ModalExpose | null>) {
  const tryOpen = (): boolean => {
    const open = modalRef.value?.open;
    if (!open) return false;
    open();
    return true;
  };

  if (tryOpen()) return;

  await nextTick();
  for (let i = 0; i < MODAL_OPEN_POLL_ATTEMPTS; i += 1) {
    if (tryOpen()) return;
    await new Promise((resolve) => {
      setTimeout(resolve, MODAL_OPEN_POLL_MS);
    });
  }
}

window.VueInstance = createVueApp({
  setup() {
    const cart = useCartStore();
    const fastBuyModal = ref<ModalExpose | null>(null);
    const cheaperModal = ref<ModalExpose | null>(null);

    const showSignOutModal = ref(false);
    const showCompleteRegistrationModal = ref(false);
    const showConfirmAddressModal = ref(false);

    provide(OPEN_FAST_BUY_MODAL_KEY, () => {
      openAsyncModal(fastBuyModal);
    });

    function initializeCart() {
      cart.initializeCart();
    }

    function reachGoalHeaderCartButton() {
      reachGoal(GOALS.HEADER_CART_BUTTON);
    }

    function closeCheaperModal() {
      openAsyncModal(cheaperModal);
    }

    function toggleOneClickModal() {
      openAsyncModal(fastBuyModal);
    }

    function toggleCompleteRegistrationModal() {
      showCompleteRegistrationModal.value = !showCompleteRegistrationModal.value;
    }

    function reachGoalFromMetaData() {
      const metaList = document.querySelectorAll<HTMLMetaElement>('[name="reach-goal"]');
      awaitForMetrikaScript().then(() => {
        metaList.forEach((meta) => {
          reachGoal(meta.content);
        });
      });
    }

    onBeforeMount(() => {
      reachGoalFromMetaData();
      // Before child mount so WarehousePositionCartControl can show pending CTA.
      if (!cart.isCartInitialized) {
        cart.initializeCart();
      }
    });

    onMounted(() => {
      if (localStorage.getItem('isFromZakaz')) {
        showCompleteRegistrationModal.value = true;
        localStorage.removeItem('isFromZakaz');
      }
    });

    return {
      fastBuyModal,
      cheaperModal,
      isCartInitialized: computed(() => cart.isCartInitialized),
      showSignOutModal,
      showCompleteRegistrationModal,
      showConfirmAddressModal,
      initializeCart,
      reachGoalHeaderCartButton,
      closeCheaperModal,
      toggleOneClickModal,
      scrollToAnchor,
      toggleCompleteRegistrationModal
    };
  },
  components: {
    PopupCart,
    VCartDropdown,
    CartNotification,
    ModalWindow,
    SearchBar,
    CookieNotice,
    VNotifications,
    HamburgerMenu,
    MobileFooterMenu,
    VProfileDropdown,
    'VCartCounter': CartCounter,
    CallbackRequestForm: asyncComponent(() => import('@components/shared/CallbackRequestForm/AccountForm.vue')),
    // Keep lazy: sync pull of HeroOfferBuy blew shop.ts gzip past 4.5 KB budget.
    // PDP shows SSR pending CTA until the island mounts (_buy_cta_pending).
    VCartControls: asyncComponent(() => import('@components/shop/WarehousePositionCartControl.vue')),
    HeroOfferBuy: asyncComponent(() => import('@components/shop/HeroOfferBuy.vue')),
    VCartView: asyncComponent(() => import('@views/shop/CartView.vue')),
    VUnauthCheckout: asyncComponent(() => import('@views/shop/CartView/UnauthCheckout.vue')),
    VMassSearch: asyncComponent(() => import('@views/shop/MassSearchView/MassSearchView.vue')),
    SearchEmptyState: asyncComponent(() => import('@components/shop/SearchEmptyState.vue')),
    AccountPopup: asyncComponent(() => import('@components/shop/Account/AccountPopup.vue')),
    PartRating: asyncComponent(() => import('@components/shop/PartRating.vue')),
    CatalogSort: asyncComponent(() => import('@components/shop/CatalogSort.vue')),
    PartDelivery: asyncComponent(() => import('@components/shop/PartDelivery.vue')),
    ShopTags: asyncComponent(() => import('@components/shop/ShopTags.vue')),
    FoundCheaperModal: asyncComponent(() => import('@components/shop/FoundCheaperModal.vue')),
    FastBuyModal: asyncComponent(() => import('../views/shop/CartView/FastBuyModal.vue')),
    RequisitesCopyButton,
    PartDeliveryTab: asyncComponent(() => import('@components/shop/PartDeliveryTab.vue')),
    PromotionsList: asyncComponent(() => import('@components/shop/PromotionsList.vue')),
    GoogleMapLoader: asyncComponent(() => import('@components/shared/GoogleMapLoader.vue')),
    ContactsFeedbackForm: asyncComponent(() => import('@components/shop/ContactsFeedbackForm.vue')),
    SSearchBar: asyncComponent(() => import('@components/landing/ui/SSearchBar.vue')),
    RegionsList: asyncComponent(() => import('@components/landing/RegionsList.vue')),
    FilterContent: asyncComponent(() => import('@views/shop/FilterContent.vue')),
    ProductList: asyncComponent(() => import('@views/shop/ProductList.vue')),
    VPagination: asyncComponent(() => import('@views/shop/ui/VPagination.vue')),
    ScSliderCategories: asyncComponent(() => import('@components/landing/SliderCategories.vue')),
    ScSliderBestsellers: asyncComponent(() => import('@components/landing/SliderBestsellers.vue')),
    SideBar: asyncComponent(() => import('@components/landing/ui/SideBar.vue')),
    SButtonMenu: asyncComponent(() => import('@components/consumer/SButtonActiveMenu.vue')),
    CompleteRegistrationModal: asyncComponent(() => import('@components/consumer/CompleteRegistrationModal')),
    PopupProductImage: asyncComponent(() => import('@components/shop/PopupProductImage.vue')),
    ProductImageSlider: asyncComponent(() => import('../views/shop/ProductImageSlider.vue'))
  }
}).mount('#vue-app');
